// Hours of Rest Export Utility - Simplified version
import { jsPDF } from 'jspdf';
import { getContext } from '../lib/context';

// Helper function to get days in a month
const getDaysInMonth = (month, year) => {
  const lastDay = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 1; i <= lastDay; i++) {
    days.push(new Date(year, month, i));
  }
  return days;
};

// Helper function to calculate monthly summary
const calculateMonthlySummary = (crewId, month, year, getDayHours) => {
  const days = getDaysInMonth(month, year);
  let totalWork = 0;
  let totalRest = 0;
  let violations = 0;
  
  days.forEach(date => {
    const dayData = getDayHours(crewId, date);
    totalWork += dayData.totalWork || 0;
    totalRest += (24 - (dayData.totalWork || 0));
    if (!dayData.isCompliant && dayData.totalWork > 0) violations++;
  });
  
  return { totalWork, totalRest, violations };
};

// Full month PDF export - detailed 24-hour grid
export const exportHoursToWindow = (
  selectedCrew, 
  currentCrewMember, 
  selectedMonth, 
  daysInMonth, 
  getDayHours, 
  summary,
  signatures = {}
) => {
  if (!selectedCrew || !currentCrewMember) return;
  
  // Create PDF in landscape orientation
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });
  
  const monthYear = selectedMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const fileName = `HOR_${currentCrewMember.full_name || currentCrewMember.name}_${monthYear}.pdf`.replace(/\s+/g, '_');
  
  // Add header with reduced spacing
  doc.setFontSize(14);
  doc.text('Hours of Rest Record', 14, 10);
  
  doc.setFontSize(10);
  doc.text(`${currentCrewMember.full_name || currentCrewMember.name} - ${currentCrewMember.position}`, 14, 16);
  doc.text(`Month: ${monthYear}`, 14, 21);
  doc.text(`Organisation: ${getContext().companyName || 'Not specified'}`, 14, 26);

  // FT-32b: Show vessel only if horVessel is set on crew member
  if (currentCrewMember.horVessel) {
    doc.text(`Vessel: ${currentCrewMember.horVessel}`, 14, 31);
  }

  doc.setFontSize(8);
  // WBC3 Edition: Always seagoing rules (horRegime removed)
  // Adjust Y position based on whether vessel line was shown
  const regimeY = currentCrewMember.horVessel ? 36 : 31;
  doc.text('Regulatory Regime: WBC3 Seagoing (Min 10h/24h, 77h/week rest)', 14, regimeY);

  // Draw simple table manually
  let yPos = currentCrewMember.horVessel ? 40 : 35;  // Adjusted for header position
  const cellWidth = 9.5;  // Reduced from 10 to fit better
  const cellHeight = 3.8;  // Much smaller rows to fit everything
  const startX = 14;
  
  doc.setFontSize(7);
  doc.setLineWidth(0.1);
  
  // Draw header row
  doc.rect(startX, yPos, 20, cellHeight);
  doc.text('Date', startX + 10, yPos + 3, { align: 'center' });
  
  // Draw hour headers
  for (let h = 0; h < 24; h++) {
    doc.rect(startX + 20 + (h * cellWidth), yPos, cellWidth, cellHeight);
    doc.text(h.toString().padStart(2, '0'), startX + 20 + (h * cellWidth) + 5, yPos + 3, { align: 'center' });
  }
  
  // Total column
  doc.rect(startX + 20 + (24 * cellWidth), yPos, 20, cellHeight);
  doc.text('Total', startX + 20 + (24 * cellWidth) + 10, yPos + 3, { align: 'center' });
  
  yPos += cellHeight;
  
  // Draw data rows
  daysInMonth.forEach(date => {
    const dayData = getDayHours(selectedCrew, date);
    const dayName = date.toLocaleDateString('en-GB', { weekday: 'short' });
    
    // Date cell
    doc.rect(startX, yPos, 20, cellHeight);
    doc.text(`${dayName} ${date.getDate()}`, startX + 1, yPos + 3);
    
    // Hour cells
    dayData.hours.forEach((hour, h) => {
      const x = startX + 20 + (h * cellWidth);
      doc.rect(x, yPos, cellWidth, cellHeight);
      
      if (hour === 'W') {
        doc.setFillColor(254, 226, 226);
        doc.rect(x, yPos, cellWidth, cellHeight, 'F');
        doc.setTextColor(185, 28, 28);
        doc.text('W', x + 5, yPos + 3, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      } else if (hour === 'R') {
        doc.setFillColor(220, 252, 231);
        doc.rect(x, yPos, cellWidth, cellHeight, 'F');
        doc.setTextColor(21, 128, 61);
        doc.text('R', x + 5, yPos + 3, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      } else {
        doc.text(hour, x + 5, yPos + 3, { align: 'center' });
      }
    });
    
    // Total cell
    doc.rect(startX + 20 + (24 * cellWidth), yPos, 20, cellHeight);
    if (!dayData.isCompliant && dayData.totalWork > 0) {
      doc.setFillColor(254, 226, 226);
      doc.rect(startX + 20 + (24 * cellWidth), yPos, 20, cellHeight, 'F');
    }
    doc.text(dayData.totalWork > 0 ? `${dayData.totalWork}h` : '-', startX + 20 + (24 * cellWidth) + 10, yPos + 3, { align: 'center' });
    
    yPos += cellHeight;
  });
  
  // Add summary below the table with proper spacing
  yPos += 2; // Minimal space after table
  doc.setFontSize(7); // Even smaller font
  doc.text(`Summary: Work ${summary.totalWork}h | Rest ${summary.totalRest}h | Violations ${summary.violations} | Legend: W=Work, R=Rest`, 14, yPos);
  
  // Add signature section at bottom
  yPos += 8;
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text('Digital Signatures', 14, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 5;
  
  // Check if we have digital signatures
  if (signatures && (signatures.crew || signatures.master)) {
    doc.setFontSize(8);
    
    // Crew signature line
    if (signatures.crew) {
      // Print name and date
      doc.text(`Crew: ${signatures.crew.signedBy}`, 14, yPos);
      doc.text(`${new Date(signatures.crew.timestamp).toLocaleDateString()}`, 14, yPos + 4);
      
      // Draw signature line
      doc.setLineWidth(0.2);
      doc.line(80, yPos + 2, 140, yPos + 2);
      
      // Add signature on the line
      if (signatures.crew.data) {
        try {
          doc.addImage(signatures.crew.data, 'PNG', 85, yPos - 10, 45, 12);
        } catch (error) {

        }
      }
    }
    
    // Authorized person signature line
    if (signatures.master) {
      const authY = signatures.crew ? yPos : yPos;
      const authX = signatures.crew ? 150 : 14;
      
      // Print name and role
      doc.text(`Auth: ${signatures.master.signedBy}`, authX, authY);
      if (signatures.master.role) {
        doc.text(`${signatures.master.role}`, authX, authY + 4);
      }
      doc.text(`${new Date(signatures.master.timestamp).toLocaleDateString()}`, authX, authY + (signatures.master.role ? 8 : 4));
      
      // Draw signature line
      doc.setLineWidth(0.2);
      doc.line(authX + 66, authY + 2, authX + 126, authY + 2);
      
      // Add signature on the line
      if (signatures.master.data) {
        try {
          doc.addImage(signatures.master.data, 'PNG', authX + 71, authY - 10, 45, 12);
        } catch (error) {

        }
      }
    }
  } else {
    // Traditional signature lines if no digital signatures
    doc.setFontSize(8);
    doc.text('Crew Member: ___________________', 14, yPos);
    doc.line(50, yPos, 110, yPos);
    
    doc.text('Authorized Person: ___________________', 150, yPos);
    doc.line(194, yPos, 254, yPos);
    
    doc.text('Date: _______________', 14, yPos + 5);
  }
  
  // Save the PDF
  doc.save(fileName);
};

// Multi-month simplified PDF export - summary view for multiple months
export const exportMultiMonthChart = (
  selectedCrew, 
  currentCrewMember, 
  startMonth, 
  monthCount = 3,
  getDayHours
) => {
  if (!selectedCrew || !currentCrewMember) return;
  
  // Create PDF in portrait orientation
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  const months = [];
  
  // Generate months to include
  for (let i = 0; i < monthCount; i++) {
    const month = new Date(startMonth);
    month.setMonth(month.getMonth() + i);
    months.push(month);
  }
  
  const startMonthName = startMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const endMonthName = months[months.length - 1].toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const fileName = `HOR_Summary_${currentCrewMember.full_name || currentCrewMember.name}_${monthCount}months.pdf`.replace(/\s+/g, '_');
  
  // Add header
  doc.setFontSize(16);
  doc.text('Hours of Rest Summary Report', 14, 15);
  
  doc.setFontSize(12);
  doc.text(`${currentCrewMember.full_name || currentCrewMember.name} - ${currentCrewMember.position}`, 14, 25);
  
  doc.setFontSize(10);
  doc.text(`Period: ${startMonthName} to ${endMonthName}`, 14, 32);
  // WBC3 Edition: Always seagoing rules (horRegime removed)
  doc.text('Regulatory Regime: WBC3 Seagoing (Min 10h/24h, 77h/week rest)', 14, 38);
  
  let yPosition = 50;
  let totalWorkHours = 0;
  let totalViolations = 0;
  
  // Process each month
  months.forEach((month, monthIndex) => {
    const monthName = month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const year = month.getFullYear();
    const monthNum = month.getMonth();
    const daysInMonth = getDaysInMonth(monthNum, year);
    const summary = calculateMonthlySummary(selectedCrew, monthNum, year, getDayHours);
    
    totalWorkHours += summary.totalWork;
    totalViolations += summary.violations;
    
    // Month header
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(monthName, 14, yPosition);
    doc.setFont(undefined, 'normal');
    
    // Draw simple summary box
    doc.setFontSize(10);
    doc.setLineWidth(0.2);
    doc.rect(14, yPosition + 5, 100, 30);
    
    doc.text(`Total Work Hours: ${summary.totalWork.toFixed(0)}h`, 20, yPosition + 12);
    doc.text(`Total Rest Hours: ${summary.totalRest.toFixed(0)}h`, 20, yPosition + 18);
    doc.text(`Violations: ${summary.violations}`, 20, yPosition + 24);
    doc.text(`Days Worked: ${daysInMonth.filter(date => getDayHours(selectedCrew, date).totalWork > 0).length}`, 20, yPosition + 30);
    
    const complianceRate = summary.violations === 0 ? '100%' : Math.round((1 - summary.violations / daysInMonth.length) * 100) + '%';
    doc.text(`Compliance Rate: ${complianceRate}`, 70, yPosition + 24);
    
    yPosition += 45;
    
    // Check if we need a new page
    if (yPosition > 250 && monthIndex < months.length - 1) {
      doc.addPage();
      yPosition = 20;
    }
  });
  
  // Overall summary
  const overallComplianceRate = totalViolations === 0 ? 100 : Math.round((1 - totalViolations / (months.length * 30)) * 100);
  
  doc.setFillColor(249, 249, 249);
  doc.rect(12, yPosition - 5, 185, 30, 'F');
  
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text(`Overall Summary (${monthCount} months)`, 14, yPosition);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  doc.text(`Total Work Hours: ${totalWorkHours.toFixed(0)}h`, 14, yPosition + 8);
  doc.text(`Total Violations: ${totalViolations}`, 14, yPosition + 15);
  doc.text(`Overall Compliance: ${overallComplianceRate}%`, 14, yPosition + 22);
  
  // Add generated timestamp
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated: ${new Date().toLocaleString()} | System: FleetSkipper HOR Module`, 14, 280);
  
  // Save the PDF
  doc.save(fileName);
};